import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../ui/dialog";
import { Pencil, Plus, Trash2, Star, Shuffle, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";

const empty = {
  alias_name: "",
  description: "",
  provider: "openrouter",
  provider_model_id: "",
  is_default: false,
  is_fallback: false,
};

export default function ModelsTab() {
  const [list, setList] = useState([]);
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [anthropicModels, setAnthropicModels] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState({});
  // Phase 8 — pricing scrape status
  const [pricingStatus, setPricingStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadPricingStatus = async () => {
    try {
      const { data } = await api.get("/admin/pricing/status");
      setPricingStatus(data);
    } catch { /* ignore — banner will simply not show */ }
  };
  useEffect(() => { loadPricingStatus(); }, []);

  const refreshPricing = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/pricing/refresh");
      const okCount = Object.values(data.providers || {}).filter((p) => p.ok).length;
      const failed = Object.entries(data.providers || {})
        .filter(([, p]) => !p.ok)
        .map(([prov, p]) => `${prov}: ${p.error || (p.used_static_fallback ? "scrape blocked → using static fallback" : "failed")}`);
      if (data.ok) {
        toast.success(`Pricing refreshed — ${okCount}/${Object.keys(data.providers || {}).length} providers updated`);
      } else if (failed.length) {
        toast.warning(`Refresh partial — ${failed.join(" · ")}`);
      } else {
        toast.error("Refresh failed");
      }
      await loadPricingStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "never";
    try {
      const d = new Date(iso);
      const diffMs = Date.now() - d.getTime();
      const hrs = Math.floor(diffMs / 36e5);
      if (hrs < 1) return "just now";
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      return `${days}d ago`;
    } catch { return iso; }
  };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/models");
      setList(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const loadCatalog = async (provider) => {
    setLoadingCatalog((s) => ({ ...s, [provider]: true }));
    try {
      const { data } = await api.get(`/admin/source-models/${provider}`);
      const items = data?.data || data?.models || data || [];
      if (provider === "openrouter") setOpenrouterModels(items);
      else setAnthropicModels(items);
    } catch (err) {
      toast.error(`Couldn't load ${provider} catalog — set the API key first.`);
    } finally {
      setLoadingCatalog((s) => ({ ...s, [provider]: false }));
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
    loadCatalog("openrouter");
    loadCatalog("anthropic");
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      alias_name: m.alias_name,
      description: m.description || "",
      provider: m.provider,
      provider_model_id: m.provider_model_id,
      is_default: !!m.is_default,
      is_fallback: !!m.is_fallback,
    });
    setOpen(true);
    loadCatalog(m.provider);
  };

  const submit = async () => {
    if (!form.alias_name.trim() || !form.provider_model_id.trim()) {
      return toast.error("Alias and model id are required");
    }
    try {
      if (editing) {
        await api.patch(`/admin/models/${editing.id}`, form);
        toast.success("Model updated");
      } else {
        await api.post("/admin/models", form);
        toast.success("Model added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete ${m.alias_name}?`)) return;
    await api.delete(`/admin/models/${m.id}`);
    load();
  };

  const catalog = form.provider === "openrouter" ? openrouterModels : anthropicModels;
  const catalogIds = catalog.map((c) => c.id).filter(Boolean);

  return (
    <div>
      {/* Phase 8 — pricing scrape status banner */}
      {pricingStatus && pricingStatus.active_errors > 0 && (
        <div className="mb-4 border border-red-300/50 dark:border-red-700/50 bg-red-50/50 dark:bg-red-950/30 rounded-sm p-3 flex items-start gap-2" data-testid="pricing-error-banner">
          <AlertTriangle className="h-4 w-4 text-red-700 dark:text-red-300 mt-0.5 flex-shrink-0" />
          <div className="text-xs flex-1">
            <div className="font-mono font-semibold text-red-800 dark:text-red-200">
              Pricing scrape needs attention
            </div>
            <div className="text-red-700/80 dark:text-red-300/80 mt-0.5">
              {pricingStatus.active_errors} provider scrape error(s). Some prices may be stale or using static fallback.
              Last successful scrape: {fmtDate(pricingStatus.last_scrape_at)}.
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-sm font-mono text-muted-foreground">{list.length} / 10 model aliases</div>
          {pricingStatus && (
            <div className="text-[10px] font-mono text-muted-foreground/70 mt-1" data-testid="pricing-status-line">
              {pricingStatus.active_errors === 0
                ? <><CheckCircle2 className="h-2.5 w-2.5 inline mr-1 text-emerald-600" />
                    Pricing live — {pricingStatus.cache_size} models cached · {fmtDate(pricingStatus.last_scrape_at)}</>
                : <>Pricing partial · {pricingStatus.cache_size} cached</>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={refreshPricing}
            variant="outline"
            disabled={refreshing}
            className="rounded-sm"
            data-testid="refresh-pricing-button"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh pricing"}
          </Button>
          <Button onClick={openCreate} className="rounded-sm" disabled={list.length >= 10} data-testid="add-model-button">
            <Plus className="h-4 w-4 mr-1.5" /> Add model
          </Button>
        </div>
      </div>

      <div className="border border-border bg-card">
        {list.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No models yet. Add up to 10 — rename them (e.g. "Shopeas 1.0") and describe what they're best for.
          </div>
        )}
        <table className="w-full">
          <thead className="border-b border-border bg-secondary/30">
            <tr className="text-left text-xs uppercase tracking-wider font-mono text-muted-foreground">
              <th className="p-3">Alias</th>
              <th className="p-3 hidden md:table-cell">Description</th>
              <th className="p-3">Provider</th>
              <th className="p-3 hidden lg:table-cell">Model ID</th>
              <th className="p-3 text-center">Default</th>
              <th className="p-3 text-center">Fallback</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((m) => (
              <tr key={m.id} className="border-b last:border-0 border-border hover:bg-muted/50" data-testid={`model-row-${m.id}`}>
                <td className="p-3 font-semibold">{m.alias_name}</td>
                <td className="p-3 hidden md:table-cell text-sm text-muted-foreground max-w-md truncate">{m.description}</td>
                <td className="p-3 text-xs uppercase font-mono">{m.provider}</td>
                <td className="p-3 hidden lg:table-cell font-mono text-xs text-muted-foreground">{m.provider_model_id}</td>
                <td className="p-3 text-center">{m.is_default && <Star className="h-4 w-4 inline text-primary fill-primary" />}</td>
                <td className="p-3 text-center">{m.is_fallback && <Shuffle className="h-4 w-4 inline text-primary" />}</td>
                <td className="p-3 text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-model-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(m)} data-testid={`delete-model-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold tracking-tight">
              {editing ? "Edit model alias" : "Add model alias"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-mono">Alias (what users see)</Label>
              <Input
                value={form.alias_name}
                onChange={(e) => setForm({ ...form, alias_name: e.target.value })}
                placeholder="Shopeas 1.0"
                className="mt-1.5 rounded-sm h-10"
                data-testid="model-alias-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-mono">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Fast model — great for quick chats"
                rows={2}
                className="mt-1.5 rounded-sm"
                data-testid="model-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-mono">Provider</Label>
                <Select value={form.provider} onValueChange={(v) => { setForm({ ...form, provider: v, provider_model_id: "" }); loadCatalog(v); }}>
                  <SelectTrigger className="mt-1.5 rounded-sm h-10" data-testid="model-provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openrouter">OpenRouter</SelectItem>
                    <SelectItem value="anthropic">Anthropic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-mono">Provider model id</Label>
                <Input
                  value={form.provider_model_id}
                  onChange={(e) => setForm({ ...form, provider_model_id: e.target.value })}
                  placeholder={form.provider === "openrouter" ? "google/gemini-2.5-flash" : "claude-sonnet-4-5"}
                  list="catalog-list"
                  className="mt-1.5 rounded-sm h-10 font-mono"
                  data-testid="model-id-input"
                />
                <datalist id="catalog-list">
                  {catalogIds.map((id) => <option key={id} value={id} />)}
                </datalist>
                <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                  {loadingCatalog[form.provider]
                    ? "loading catalog…"
                    : catalogIds.length
                    ? `${catalogIds.length} models in catalog (type to autocomplete)`
                    : "Set the API key to load catalog."}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between border border-border p-3 rounded-sm">
              <div>
                <div className="text-sm font-medium">Default</div>
                <div className="text-xs text-muted-foreground">Used when users haven't picked one.</div>
              </div>
              <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="model-default-switch" />
            </div>
            <div className="flex items-center justify-between border border-border p-3 rounded-sm">
              <div>
                <div className="text-sm font-medium">Fallback</div>
                <div className="text-xs text-muted-foreground">Used when the default fails (informational).</div>
              </div>
              <Switch checked={form.is_fallback} onCheckedChange={(v) => setForm({ ...form, is_fallback: v })} data-testid="model-fallback-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} className="rounded-sm" data-testid="model-save-button">
              {editing ? "Save changes" : "Add model"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
